"use client";

import React from "react";
import styles from "./PartnershipCardPointsCalculator.module.scss";
import Text from "@JohnLewisPartnership/jlfs-ui-library/dist/components/inputs/text/text";
import Message from "@JohnLewisPartnership/jlfs-ui-library/dist/components/message/message";
import Icon from "@JohnLewisPartnership/jlfs-ui-library/dist/components/icon/icon";

type PartnershipCardPointsCalculatorCCProps = {
  jlpSpendMultiplier: number;
  elsewhereSpendMultiplier: number;
  largePurchaseSpendMultiplier: number;
  largePurchaseSpendHelpText: string;
  totalValueHeadingText: string;
  maximumAnnualVoucherValue: number;
  maximumValueExceededWarningText: string;
  footer?: React.ReactNode;
};

const MAX_FIELD_VALUE = 99999;

export const calculatePoints = (
  jlpSpend: number,
  elsewhereSpend: number,
  largePurchaseSpend: number,
  jlpSpendMultiplier: number,
  elsewhereSpendMultiplier: number,
  largePurchaseSpendMultiplier: number
) => {
  const jlpSpendPoints = Math.floor(jlpSpend * 12 * jlpSpendMultiplier);
  const elsewhereSpendPoints = Math.floor(
    elsewhereSpend * 12 * elsewhereSpendMultiplier
  );
  const largePurchaseSpendPoints = Math.floor(
    largePurchaseSpend * largePurchaseSpendMultiplier
  );

  return {
    jlpSpendPoints,
    elsewhereSpendPoints,
    largePurchaseSpendPoints,
    totalAnnualVoucherPoints:
      jlpSpendPoints + elsewhereSpendPoints + largePurchaseSpendPoints,
  };
}

const PartnershipCardPointsCalculatorCC = ({
  jlpSpendMultiplier,
  elsewhereSpendMultiplier, // £10 = 1pt
  largePurchaseSpendMultiplier,
  largePurchaseSpendHelpText,
  totalValueHeadingText,
  maximumAnnualVoucherValue = 1000,
  maximumValueExceededWarningText,
  footer,
}: PartnershipCardPointsCalculatorCCProps) => {
  const [jlpSpend, setJlpSpend] = React.useState(0);
  const [elsewhereSpend, setElsewhereSpend] = React.useState(0);
  const [largePurchaseSpend, setLargePurchaseSpend] = React.useState(0);

  const {
    totalAnnualVoucherPoints,
  } = calculatePoints(
    jlpSpend,
    elsewhereSpend,
    largePurchaseSpend,
    jlpSpendMultiplier,
    elsewhereSpendMultiplier,
    largePurchaseSpendMultiplier
  );

  const totalAnnualVoucherValue = totalAnnualVoucherPoints / 100;

  const isMaxExceeded = totalAnnualVoucherValue > maximumAnnualVoucherValue;

  const totalAnnualVoucherDisplayValue = isMaxExceeded
    ? maximumAnnualVoucherValue
    : Math.round(totalAnnualVoucherValue);

  const updateInputState = (inputValue: string, stateSetter: any) => {
    const value = stripCurrency(inputValue);
    if (value < 0) {
      stateSetter(0);
    } else if (value > MAX_FIELD_VALUE) {
      stateSetter(MAX_FIELD_VALUE);
    } else {
      stateSetter(value);
    }
  };

  const stripCurrency = (value: string) => {
    const strippedValue = value.replace(/[^0-9]/g, "");
    return strippedValue ? parseInt(strippedValue) : 0;
  };

  return (
    <div className={styles.partnershipCardPointsCalculator}>
      <div>
        <div className={styles.calculatorInput}>
          <p>
            Average <strong>monthly</strong> spend at Waitrose and John Lewis
          </p>

          <Text
            name="jlpSpend"
            type="text"
            inputMode="numeric"
            value={"£" + jlpSpend.toString()}
            onChange={(e) => updateInputState(e.target.value, setJlpSpend)}
          />
        </div>
        <div className={styles.calculatorInput}>
          <p>
            Average <strong>monthly</strong> spend elsewhere
          </p>
          <Text
            name="elsewhereSpend"
            type="text"
            inputMode="numeric"
            value={"£" + elsewhereSpend.toString()}
            onChange={(e) =>
              updateInputState(e.target.value, setElsewhereSpend)
            }
          />
        </div>
        <div className={styles.calculatorInput}>
          <p>
            Average <strong>annual</strong> spend on large one-off purchases
          </p>
          <p className={styles.largePurchaseSpendInfo}>
            <Icon name="info" />
            {largePurchaseSpendHelpText}
          </p>

          <Text
            name="largePurchaseSpend"
            type="text"
            inputMode="numeric"
            value={"£" + largePurchaseSpend.toString()}
            onChange={(e) =>
              updateInputState(e.target.value, setLargePurchaseSpend)
            }
          />
        </div>
      </div>
      <div>
        <div className={styles.totalAnnualVoucherValue}>
          <p className={styles.totalAnnualVoucherValueTitle}>
            {totalValueHeadingText}
          </p>
          <div className={styles.totalAnnualVoucherValueAmount}>
            £{totalAnnualVoucherDisplayValue.toLocaleString("en-GB")}
          </div>
        </div>
        {isMaxExceeded && (
          <Message type="information" title={maximumValueExceededWarningText} />
        )}

        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
};

export default PartnershipCardPointsCalculatorCC;
